import type { CaseData, CmsPage, ImageData } from "@/cms/models";

export type CaseGalleryViewer = "scroll" | "slideshow";
export type CaseGalleryViewerSetting = NonNullable<
  CaseGallerySettings["viewer"]
>;
export type CaseGalleryMedia = "photo" | "drawing" | "video";

export interface CaseGallerySettings {
  numberOfImages?: string;
  videolinkIconName?: string;
  presentation?: "cinematic";
  /** "none" keeps the photos on the page but lets clicks scroll to the page's CaseImageGridSection. */
  viewer?: CaseGalleryViewer | "none";
}

export function resolveCaseGalleryViewer(
  settings: Pick<CaseGallerySettings, "presentation" | "viewer">,
): CaseGalleryViewerSetting {
  if (
    settings.viewer === "scroll" ||
    settings.viewer === "slideshow" ||
    settings.viewer === "none"
  )
    return settings.viewer;
  return defaultCaseGalleryViewer(settings);
}

/** The viewer a gallery opens when nothing overrides it; floor plans and video keep it with "none". */
export function defaultCaseGalleryViewer(
  settings: Pick<CaseGallerySettings, "presentation">,
): CaseGalleryViewer {
  return settings.presentation === "cinematic" ? "slideshow" : "scroll";
}

export const CASE_IMAGE_GRID_ANCHOR = "caseImageGridSection";

export function pageHasCaseImageGrid(page?: Pick<CmsPage, "sections"> | null) {
  return !!page?.sections?.some(
    (section) => section?.type === "CaseImageGridSection",
  );
}

export function scrollToCaseImageGrid() {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document
    .getElementById(CASE_IMAGE_GRID_ANCHOR)
    ?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

export function getCaseGalleryImages(
  caseData: Pick<
    CaseData,
    "images" | "imageUrl" | "advertisementAddressOrDisplayName"
  >,
): ImageData[] {
  if (caseData.images.some((image) => image.namespace.endsWith("photo")))
    return caseData.images;
  if (!caseData.imageUrl) return caseData.images;
  return [
    {
      xlUrl: caseData.imageUrl,
      lgUrl: caseData.imageUrl,
      mdUrl: caseData.imageUrl,
      namespace: "photo",
      description: caseData.advertisementAddressOrDisplayName,
      pixelWidth: 0,
      pixelHeight: 0,
      priority: 0,
    },
    ...caseData.images,
  ];
}
